import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Shield, FileText } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { fetchConfig } from "@/lib/api";

export default function AvisoPrivacidad() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetchConfig().then(setConfig).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recursos = config?.recursos_oficiales || [];
  const integral = recursos.find((r) => r.key === "aviso_privacidad_integral");
  const simplificado = recursos.find(
    (r) => r.key === "aviso_privacidad_simplificado"
  );

  return (
    <div className="min-h-screen flex flex-col bg-eje/[0.02]">
      <Header variant="light" />

      <main className="container-narrow py-12 md:py-16 flex-1">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-eje hover:text-gold-600 text-sm mb-6"
          data-testid="aviso-back"
        >
          <ArrowLeft className="w-4 h-4" /> Inicio
        </Link>

        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 bg-eje text-white rounded-sm flex items-center justify-center flex-shrink-0">
            <Shield className="w-6 h-6" />
          </div>
          <div>
            <div className="overline text-eje mb-1">
              Tratamiento de datos personales
            </div>
            <h1
              className="font-serif text-3xl md:text-4xl text-eje"
              data-testid="aviso-title"
            >
              Aviso de privacidad
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              Programa de Posdoctorado y Estancias de Investigación 2026 ·
              Escuela Judicial Electoral
            </p>
          </div>
        </div>

        {/* Resumen */}
        <article className="card-eje p-7 md:p-9 mb-6 prose prose-sm max-w-none">
          <h2 className="font-serif text-xl text-eje mb-3 mt-0">
            Resumen del tratamiento
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            La <strong>Escuela Judicial Electoral (EJE) del Tribunal Electoral
            del Poder Judicial de la Federación</strong>, con domicilio en
            Carlota Armero núm. 5000, Colonia CTM Culhuacán, Demarcación
            Territorial Coyoacán, C.P. 04480, Ciudad de México, es responsable
            del tratamiento de los datos personales que usted proporcione al
            inscribirse en la presente convocatoria.
          </p>

          <h3 className="font-serif text-lg text-eje mt-6 mb-2">
            Finalidades del tratamiento
          </h3>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1.5 leading-relaxed">
            <li>
              Registrar y dar seguimiento a su solicitud de inscripción al
              Posdoctorado o Estancia de Investigación 2026.
            </li>
            <li>
              Evaluar académica y administrativamente su candidatura, así como
              integrar el expediente del proceso de selección.
            </li>
            <li>
              Comunicar resultados, generar constancias y dar continuidad a la
              relación académica durante y al término del programa.
            </li>
            <li>
              Cumplir con las obligaciones de archivo, transparencia y rendición
              de cuentas aplicables al TEPJF.
            </li>
          </ul>

          <h3 className="font-serif text-lg text-eje mt-6 mb-2">
            Datos personales que recabamos
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            Datos de identificación y contacto (nombre, fecha de nacimiento,
            nacionalidad, CURP, RFC, identificación oficial, correo
            electrónico, teléfono, domicilio), datos académicos (grado, título,
            institución, publicaciones) y los documentos electrónicos que
            integran su expediente de postulación.
          </p>

          <h3 className="font-serif text-lg text-eje mt-6 mb-2">
            Transferencias
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            Sus datos podrán ser transferidos a las áreas competentes del TEPJF
            y a la Junta Académica de la EJE para los fines descritos, sin
            requerirse su consentimiento conforme a la normatividad aplicable.
            No se realizan transferencias adicionales a terceros sin su
            consentimiento previo.
          </p>

          <h3 className="font-serif text-lg text-eje mt-6 mb-2">
            Derechos ARCO
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            Usted puede ejercer sus derechos de Acceso, Rectificación,
            Cancelación y Oposición (ARCO) ante la Unidad de Transparencia del
            TEPJF, conforme a la Ley General de Protección de Datos Personales
            en Posesión de Sujetos Obligados.
          </p>

          <h3 className="font-serif text-lg text-eje mt-6 mb-2">
            Cambios al aviso
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            El presente aviso podrá ser modificado. Las actualizaciones se
            publicarán en{" "}
            <a
              href="https://www.te.gob.mx/eje/front/postdoctorado"
              target="_blank"
              rel="noreferrer"
              className="text-eje underline"
            >
              www.te.gob.mx/eje/front/postdoctorado
            </a>
            .
          </p>
        </article>

        {/* Official documents */}
        <div className="card-eje p-6 mb-10">
          <h2 className="overline text-eje mb-4">Documentos oficiales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {integral && (
              <DocLink
                title="Aviso de privacidad integral"
                description="Texto completo conforme a la LGPDPPSO."
                href={integral.url}
                testid="link-aviso-integral"
              />
            )}
            {simplificado && (
              <DocLink
                title="Aviso de privacidad simplificado"
                description="Versión abreviada para consulta rápida."
                href={simplificado.url}
                testid="link-aviso-simplificado"
              />
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function DocLink({ title, description, href, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-start gap-3 p-4 border border-slate-200 rounded-sm hover:border-eje hover:bg-eje/[0.02] transition-colors group"
      data-testid={testid}
    >
      <div className="w-9 h-9 bg-eje/5 border border-eje/10 text-eje flex items-center justify-center rounded-sm flex-shrink-0">
        <FileText className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-eje group-hover:underline flex items-center gap-1.5">
          {title}
          <ExternalLink className="w-3.5 h-3.5" />
        </div>
        <div className="text-xs text-slate-600 mt-0.5">{description}</div>
      </div>
    </a>
  );
}
